import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import type { EpgChannel } from '@jwp/ott-common/types/epg';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { getJustFinishedPlaylistFromChannels, getLivePlaylistFromChannels, getSoonToStartPlaylistFromChannels } from '@jwp/ott-common/src/utils/epg';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import LoadingOverlay from '@jwp/ott-ui-react/src/components/LoadingOverlay/LoadingOverlay';

import Shelf from '../../components/Shelf/Shelf';
import styles from '../../containers/ShelfList/ShelfList.module.scss'; // Reuse to prevent duplicate styling

// Shelf containers are needed for padding and chevron button positioning
const Section = ({ children, title }: { children: React.ReactNode; title: string }) => (
  <section className={styles.shelfContainer} aria-label={title}>
    {children}
  </section>
);

type Props = {
  channels: EpgChannel[];
  getProgramUrl: (channelId: string, programId: string) => string | undefined;
  className?: string;
  isFetching: boolean;
};

const EpgLiveNow: React.FC<Props> = ({ channels, className, isFetching, getProgramUrl }: Props) => {
  const { t } = useTranslation('epg');
  const { accessModel } = useConfigStore(({ accessModel }) => ({ accessModel }), shallow);
  const { hasSubscription, isLoggedIn } = useAccountStore(({ user, subscription }) => ({ hasSubscription: !!subscription, isLoggedIn: !!user }), shallow);

  const liveNowPrograms = useMemo(() => getLivePlaylistFromChannels(t('live_now'), channels), [t, channels]);
  const soonToStartPrograms = useMemo(() => getSoonToStartPlaylistFromChannels(t('scheduled'), channels), [t, channels]);
  const justFinishedPrograms = useMemo(() => getJustFinishedPlaylistFromChannels(t('replay'), channels), [t, channels]);

  const getCardUrl = (item: PlaylistItem) => {
    // CUSTOMER vd-ott-iq specific metadata fields
    const { channelId, programId } = item.metadata as { channelId: string; programId: string };
    return getProgramUrl(channelId, programId);
  };

  if (isFetching) {
    return <LoadingOverlay inline />;
  }

  return (
    <div className={className}>
      <Section title={t('live_now')}>
        <Shelf
          playlist={liveNowPrograms}
          type="playlist"
          accessModel={accessModel}
          isLoggedIn={isLoggedIn}
          hasSubscription={hasSubscription}
          getUrl={getCardUrl}
        />
      </Section>
      <Section title={t('scheduled')}>
        <Shelf
          playlist={soonToStartPrograms}
          type="playlist"
          accessModel={accessModel}
          isLoggedIn={isLoggedIn}
          hasSubscription={hasSubscription}
          getUrl={getCardUrl}
        />
      </Section>
      <Section title={t('replay')}>
        <Shelf
          playlist={justFinishedPrograms}
          type="playlist"
          accessModel={accessModel}
          isLoggedIn={isLoggedIn}
          hasSubscription={hasSubscription}
          getUrl={getCardUrl}
        />
      </Section>
    </div>
  );
};

export default EpgLiveNow;
